import gql from 'graphql-tag';
import client from '../../components/ApolloClient';
import { ASSESSMENT_META_FRAGMENT, ASSESSMENT_RESPONSE_BODY_FRAGMENT } from '../../utils/fragments';

const GET_ASSESSMENT_MEMBER_RENDER = gql`
  query GetAssessmentMemberRender($assessmentUuid: String!, $patientId: String!) {
    getAssessmentMemberRender(assessmentUuid: $assessmentUuid, patientId: $patientId) {
      id
      type
      question {
        questionId
        title
        questionType
        dataType
        isRequired
        options {
          optionId
          label
          displayOrder
          actions {
            actionType
            config
          }
        }
      }
      group {
        groupId
        groupName
        isImported
        questions {
          questionId
          title
          questionType
          dataType
          isRequired
          options {
            optionId
            label
            displayOrder
            actions {
              actionType
              config
            }
          }
        }
      }
    }
  }
`;

const GET_ASSESSMENT_SUBMISSION_RESPONSE = gql`
  query GetAssessmentSubmissionResponse($assessmentSubmissionId: String!) {
    getAssessmentSubmissionResponse(assessmentSubmissionId: $assessmentSubmissionId) {
      stepId
      type
      question {
        questionId
        title
        questionType
        dataType
        isRequired
        isAnswered
        answer {
          selectedOptions {
            optionId
            label
            riskScore
          }
          textValue
          totalRiskScore
        }
        options {
          optionId
          label
          displayOrder
          actions {
            actionType
            config
          }
        }
      }
      group {
        groupId
        groupName
        questions {
          questionId
          title
          questionType
          dataType
          isRequired
          isAnswered
          answer {
            selectedOptions {
              optionId
              label
              riskScore
            }
            textValue
            totalRiskScore
          }
          options {
            optionId
            label
            displayOrder
            actions {
              actionType
              config
            }
          }
        }
      }
    }
  }
`;

const SUBMIT_ASSESSMENT = gql`
  mutation SubmitAssessment(
    $assessmentUuid: String!
    $submissionId: String!
    $request: AssessmentSubmissionRequestInput!
  ) {
    submitAssessment(
      assessmentUuid: $assessmentUuid
      submissionId: $submissionId
      request: $request
    ) {
      stepId
      type
    }
  }
`;

const SAVE_ASSESSMENT_QUESTION_RESPONSE = gql`
  mutation SaveAssessmentQuestionResponse(
    $assessmentSubmissionId: String!
    $request: AssessmentSubmissionConfigQuestionInput!
  ) {
    saveAssessmentQuestionResponse(
      assessmentSubmissionId: $assessmentSubmissionId
      request: $request
    ) {
      statusMessage
    }
  }
`;

export const getAssessmentMemberRender = async (assessmentUuid, patientId) => {
  try {
    const result = await client.query({
      query: GET_ASSESSMENT_MEMBER_RENDER,
      variables: { assessmentUuid, patientId },
      fetchPolicy: 'no-cache',
    });
    return result?.data?.getAssessmentMemberRender || [];
  } catch (error) {
    console.error('Error fetching assessment member render', error);
    throw error;
  }
};

export const getAssessmentSubmissionResponse = async (assessmentSubmissionId) => {
  try {
    const result = await client.query({
      query: GET_ASSESSMENT_SUBMISSION_RESPONSE,
      variables: { assessmentSubmissionId },
      fetchPolicy: 'no-cache',
    });
    return result?.data?.getAssessmentSubmissionResponse;
  } catch (error) {
    console.error('Error fetching assessment submission response', error);
    throw error;
  }
};

export const submitAssessment = async (assessmentUuid, submissionId, request) => {
  try {
    const result = await client.mutate({
      mutation: SUBMIT_ASSESSMENT,
      variables: { assessmentUuid, submissionId, request },
    });
    return result?.data?.submitAssessment;
  } catch (error) {
    console.error('Error submitting assessment', error);
    throw error;
  }
};

export const saveAssessmentQuestionResponse = async (assessmentSubmissionId, request) => {
  try {
    const result = await client.mutate({
      mutation: SAVE_ASSESSMENT_QUESTION_RESPONSE,
      variables: { assessmentSubmissionId, request },
    });
    return result?.data?.saveAssessmentQuestionResponse;
  } catch (error) {
    console.error('Error saving assessment question response', error);
    throw error;
  }
};

const UPDATE_ASSESSMENT = gql`
  mutation updateAssessment($assessmentRequest: AssessmentRequest) {
    updateAssessment(assessmentRequest: $assessmentRequest) {
      assessmentMeta {
        ...assessmentMeta
      }
      assessmentResponseBody {
        ...assessmentResponseBody
      }
      pageableResponse {
        count
        offset
        total
      }
    }
  }
  ${ASSESSMENT_META_FRAGMENT}
  ${ASSESSMENT_RESPONSE_BODY_FRAGMENT}
`;

const UPDATE_ASSESSMENT_STATUS = gql`
mutation UpdateAssessmentStatus($assessmentRequest: AssessmentRequest!) {
  updateAssessmentStatus(assessmentRequest: $assessmentRequest) {
    statusMessage
  }
}
`;

export const updateAssessmentStatus = async (assessmentInstanceId, status) => {
  try {
    const result = await client.mutate({
      mutation: UPDATE_ASSESSMENT_STATUS,
      variables: {
        assessmentRequest: {
          assessmentInstanceId,
          status,
        },
      },
    });
    return result?.data?.updateAssessmentStatus;
  } catch (error) {
    console.error('Error updating the status', error);
  }
};

export const AssessmentServices = {
  updateAssessment(args, mutation = UPDATE_ASSESSMENT) {
    return new Promise((resolve, reject) => {
      client
        .mutate({
          mutation,
          variables: {
            assessmentRequest: {
              ...args,
            },
          },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          resolve(response.data.updateAssessment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};
